/**
 * Hook for approval mutations (decisions, assignee)
 */
import { useContext, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { endpoints } from "../../../../../endpoints";
import type {
  ApprovalAssigneeUpdatePayload,
  ApprovalUpdatePayload,
  ApprovalWorkflow,
} from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";

export function useApprovalMutations() {
  const { storefront_id } = useStoreState();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitDecision = async (
    approvalId: string,
    payload: ApprovalUpdatePayload
  ): Promise<ApprovalWorkflow | null> => {
    if (!storefront_id) return null;

    setIsSubmitting(true);
    try {
      const response = await axios.patch<ApprovalWorkflow>(
        endpoints.v2_storefronts_id_integrations_approvals_id(
          storefront_id,
          approvalId
        ),
        payload
      );

      const message =
        payload.decision === "approve"
          ? t("Approval submitted successfully")
          : t("Rejection submitted successfully");
      notifySuccess(message);

      return response.data;
    } catch (err) {
      notifyError(t("Failed to submit decision"), { error: err });
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateAssignee = async (
    approvalId: string,
    payload: ApprovalAssigneeUpdatePayload
  ): Promise<ApprovalWorkflow | null> => {
    if (!storefront_id) return null;

    setIsSubmitting(true);
    try {
      const response = await axios.patch<ApprovalWorkflow>(
        endpoints.v2_storefronts_id_integrations_approvals_id_assignee(
          storefront_id,
          approvalId
        ),
        payload
      );
      return response.data;
    } catch (err) {
      notifyError(t("Failed to update assignee"), { error: err });
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitDecision,
    updateAssignee,
    isSubmitting,
  };
}
